import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildCountryGroups,
  filterCountryGroups,
  type CountryGroup,
  type CountryLabels,
  type CountryLeague,
} from '@/utils/leagueCountries';

export interface CountryGroups<T extends CountryLeague> {
  /** Every country bucket, sorted for the current UI language. */
  groups: CountryGroup<T>[];
  /** `groups` narrowed by the client-side search (same array when `query` is blank). */
  visibleGroups: CountryGroup<T>[];
}

/**
 * Buckets leagues into localized, sorted country groups (`utils/leagueCountries`)
 * and applies the client-side search. Labels come from the `league` namespace.
 */
export function useCountryGroups<T extends CountryLeague>(
  leagues: readonly T[],
  query: string,
): CountryGroups<T> {
  const { t, i18n } = useTranslation('league');
  const lang = i18n.language;

  const groups = useMemo(() => {
    const labels: CountryLabels = {
      overrides: t('league:countries', { returnObjects: true }) as Record<string, string>,
      other: t('league:otherCountry'),
    };
    return buildCountryGroups(leagues, lang, labels);
  }, [leagues, lang, t]);

  const visibleGroups = useMemo(() => filterCountryGroups(groups, query), [groups, query]);

  return { groups, visibleGroups };
}
